import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { MatSnackBar } from "@angular/material";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
// Import  TranslateService
import { TranslateService } from "@ngx-translate/core";
import { AssessmentAnswerInterface } from "app/assessment/models";
import { MceSettings } from "app/core/mce-config/mce-editor-config.constant";
import { UserInterface } from "app/core/user/models/user.model";
import { InstalledStateEnum } from "app/equipment/enums/installed-state.enum";
import {
  EmployeeEquipmentInterface,
  EmployeeEquipmentWorkflowInterface,
  EmployeeEquipmentWorkflowStatusInterface,
  EmployeeEquipmentWorkflowStatusTypeInterface,
  EquipmentCategoryInterface,
  EquipmentInterface,
} from "app/equipment/models";
import { EquipmentService } from "app/equipment/services";
import {
  stringDecodeToHtml,
  stringEncodeToB64,
} from "app/shared/utilities/utility-methods.utility";
import { environment } from "environments/environment.prod";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";

@Component({
  selector: "cz-equipment-dialog",
  templateUrl: "./equipment-dialog.component.html",
  styleUrls: ["./equipment-dialog.component.scss"],
})
export class EquipmentDialogComponent implements OnInit, OnDestroy {
  public readonly installedState: string = InstalledStateEnum.StateInstalled;
  public readonly recommendedState: string =
    InstalledStateEnum.StateRecommended;
  public readonly removedState: string = InstalledStateEnum.StateRemoved;

  public tinyApiKey: string = environment.tinyMceKey;
  public editorConfig: any = MceSettings.default;
  public descriptionDisplayConfig: any = MceSettings.readonlyDisplay;

  // Simple conditionals for showing markup
  public editInstalledEquipment: boolean = false;
  public addInstalledEquipment: boolean = false;
  public editRecommendedEquipment: boolean = false;
  public addRecommendedEquipment: boolean = false;
  public showRecommendedFields: boolean = false;
  public fieldState: string;
  public removedDeleted: boolean = false;
  public equipmentAvailable: boolean = false;

  // Service delivered values
  public workAreas: AssessmentAnswerInterface[];
  public equipmentLibrary: EquipmentCategoryInterface[];
  public statusList: EmployeeEquipmentWorkflowStatusTypeInterface[];
  public filteredStatusList: EmployeeEquipmentWorkflowInterface[];
  public categoryList: { id: number; title: string }[];
  public categoryEquipmentList: EmployeeEquipmentInterface[];
  public currentStatus: EmployeeEquipmentWorkflowStatusInterface;
  public statusHistory: EmployeeEquipmentWorkflowStatusInterface[];
  public currentStatusUser: UserInterface;

  // Forms and controls
  public categoryItemFormGroup: FormGroup;
  public workAreaStatusFormGroup: FormGroup;
  public accessoryFieldsFormGroup: FormGroup;
  public categoryControl: FormControl;
  public itemControl: FormControl;
  public workAreaControl: FormControl;
  public statusControl: FormControl;
  public statusNoteControl: FormControl;
  public statusDateControl: FormControl;
  public descriptionControl: FormControl;

  public workflow: EmployeeEquipmentWorkflowInterface;
  public origin: string;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  // Inject TranslateService and CookieService
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      workFlow: EmployeeEquipmentWorkflowInterface;
      origin: string;
      authUser: UserInterface;
      esppId: number;
      workAreas: AssessmentAnswerInterface[];
    },
    private readonly _equipmentService: EquipmentService,
    private _translateService: TranslateService,
    private _formBuilder: FormBuilder,
    private readonly _snackBar: MatSnackBar,
    private _dialogRef: MatDialogRef<EquipmentDialogComponent>,
  ) {
    this.workflow = this.data.workFlow;
    this.origin = this.data.origin;
    this.workAreas = this.data.workAreas;

    this.editorConfig = { ...this.editorConfig, height: 130 };
    this.initializeFormsAndControls();
  }

  public ngOnInit(): void {
    this.setFormMode();

    this.initializeSubscriptions();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public deleteItem(): void {
    this._equipmentService
      .deleteEmployeeEquipmentWorkflow(this.workflow)
      .pipe(take(1))
      .subscribe(() => {
        // Static text to Language Variable updated
        this.showSnackBar(
          this.workflow.equipmentItem.title +
            this._translateService.instant("$message_deleted"),
        );
        this._dialogRef.close();
      });
  }

  public saveWorkflow(): void {
    if (this.categoryItemFormGroup.invalid) {
      this.categoryControl.markAsTouched();
      // So why?  because the secondary item control is dependant on the category control
      // I don't check it's validation or set it's error state until the first control passes
      // validation
      if (this.categoryControl.valid) {
        this.itemControl.markAsTouched();
      }
      return;
    }

    if (this.workAreaStatusFormGroup.invalid) {
      this.statusControl.markAsTouched();
      this.workAreaControl.markAsTouched();
      return;
    }

    // set the notes field on the current status
    this.currentStatus = {
      ...this.currentStatus,
      notes: stringEncodeToB64(this.statusNoteControl.value),
    };

    if (this.workflow.id > 0) {
      // update the equipment from the accessory form
      const updatedEquipmentItem: EmployeeEquipmentInterface = {
        ...this.workflow.equipmentItem,
        assetId: this.accessoryFieldsFormGroup.get("assetIdControl").value,
        color: this.accessoryFieldsFormGroup.get("colorControl").value,
        cost: this.accessoryFieldsFormGroup.get("costControl").value,
        serialNumber: this.accessoryFieldsFormGroup.get("serialNumberControl")
          .value,
      };
      this.workflow = {
        ...this.workflow,
        equipmentItem: updatedEquipmentItem,
      };
      // if the status enum first set values are not set yet, then set them
      switch (this.currentStatus.realatedStateEnum) {
        case InstalledStateEnum.StateRecommended:
          this.workflow = {
            ...this.workflow,
            equipmentItem: {
              ...this.workflow.equipmentItem,
              recommendedDate: this.statusDateControl.value,
            },
          };
          break;
        case InstalledStateEnum.StateInstalled:
          this.workflow = {
            ...this.workflow,
            equipmentItem: {
              ...this.workflow.equipmentItem,
              installedDate: this.statusDateControl.value,
            },
          };
          break;

        case InstalledStateEnum.StateRemoved:
          this.workflow = {
            ...this.workflow,
            equipmentItem: {
              ...this.workflow.equipmentItem,
              removedDate: this.statusDateControl.value,
            },
          };
          break;
      }

      this._equipmentService
        .updateEmployeeEquipmentItem(this.workflow)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (updatedEquipmentWorkflow: EmployeeEquipmentWorkflowInterface) => {
            if (!updatedEquipmentWorkflow) {
              return;
            }
            if (!this.currentStatus.id) {
              this._equipmentService
                .addStatusToEmployeeEquipmentWorkflow(
                  this.workflow,
                  this.currentStatus,
                )
                .pipe(take(1))
                .subscribe(
                  (updatedWorkflow: EmployeeEquipmentWorkflowInterface) => {
                    if (!updatedWorkflow) {
                      return;
                    }
                  },
                );
            } else {
              // update existing status
              this._equipmentService
                .updateEmployeeEquipmentWorkflowStatus(
                  this.workflow,
                  this.currentStatus,
                )
                .pipe(take(1))
                .subscribe(
                  (updatedWorkflow: EmployeeEquipmentWorkflowInterface) => {
                    if (!updatedWorkflow) {
                      return;
                    }
                  },
                );
            }
          },
        );
    } else {
      // New workflow path
      // set the correct user accessible date on the equipment
      if (
        this.currentStatus.realatedStateEnum ===
        InstalledStateEnum.StateInstalled
      ) {
        this.workflow.equipmentItem = {
          ...this.workflow.equipmentItem,
          installedDate: this.statusDateControl.value,
        };
      } else if (
        this.currentStatus.realatedStateEnum ===
        InstalledStateEnum.StateRecommended
      ) {
        this.workflow.equipmentItem = {
          ...this.workflow.equipmentItem,
          recommendedDate: this.statusDateControl.value,
        };
      } else {
        this.workflow.equipmentItem = {
          ...this.workflow.equipmentItem,
          removedDate: this.statusDateControl.value,
        };
      }
      // add the current status to the status history
      this.workflow = {
        ...this.workflow,
        statusHistory: [this.currentStatus],
      };

      // add the evaluation id if there is one
      this.workflow = {
        ...this.workflow,
        esppId: this.data.esppId,
      };

      this._equipmentService
        .createEmployeeEquipmentWorkflow(this.workflow)
        .pipe(take(1))
        .subscribe((updatedWorkflow: EmployeeEquipmentWorkflowInterface) => {
          if (!updatedWorkflow) {
            return;
          }
        });
    }
    // Static text to Language Variable updated
    this.showSnackBar(
      this.workflow.equipmentItem.title +
        this._translateService.instant("$message_saved"),
    );
    this._dialogRef.close();
  }

  public getEquipmentDescription(
    equipmentItem: EmployeeEquipmentInterface,
  ): string {
    if (!equipmentItem || !equipmentItem.description) {
      return "";
    }
    return stringDecodeToHtml(equipmentItem.description);
  }

  public getStatusDescription(
    status: EmployeeEquipmentWorkflowStatusInterface,
  ): string {
    return stringDecodeToHtml(status && status.notes ? status.notes : "");
  }

  public showSnackBar(message: string): void {
    this._snackBar.open(message, "", {
      duration: 2000,
    });
  }

  private setFormMode(): void {
    if (this.workflow && this.workflow.id > 0) {
      this.addInstalledEquipment = false;
      this.addRecommendedEquipment = false;

      if (this.origin === InstalledStateEnum.StateInstalled) {
        this.editInstalledEquipment = true;
        this.editRecommendedEquipment = false;
      } else {
        this.editInstalledEquipment = false;
        this.editRecommendedEquipment = true;
      }
    } else {
      this.editInstalledEquipment = false;
      this.editRecommendedEquipment = false;

      if (this.origin === InstalledStateEnum.StateInstalled) {
        this.addInstalledEquipment = true;
        this.addRecommendedEquipment = false;
      } else {
        this.addInstalledEquipment = false;
        this.addRecommendedEquipment = true;
      }
    }
  }

  private initializeSubscriptions(): void {
    // Service subscriptions
    if (this.data.origin === InstalledStateEnum.StateInstalled) {
      this._equipmentService
        .getEquipmentLibrary()
        .pipe(takeUntil(this.destroy$))
        .subscribe((fullLibraryData: EquipmentCategoryInterface[]) => {
          if (!fullLibraryData) {
            return;
          }

          this.equipmentLibrary = fullLibraryData.filter(
            (category: EquipmentCategoryInterface) => {
              if (category.items && category.items.length > 0) {
                this.equipmentAvailable = true;
                return category;
              }
            },
          );

          this.setFormControlValues();
        });
    } else {
      this._equipmentService
        .getRecommendedEquipmentLibrary()
        .pipe(takeUntil(this.destroy$))
        .subscribe((recommendedLibraryData: EquipmentCategoryInterface[]) => {
          if (!recommendedLibraryData) {
            return;
          }

          this.equipmentLibrary = recommendedLibraryData.map(
            (category: EquipmentCategoryInterface) => {
              if (category.items && category.items.length > 0) {
                this.equipmentAvailable = true;
                return category;
              }
            },
          );

          this.setFormControlValues();
        });
    }

    this._equipmentService
      .getEquipmentWorkflowStatusTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (statusTypes: EmployeeEquipmentWorkflowStatusTypeInterface[]) => {
          if (!statusTypes) {
            return;
          }

          this.statusList = statusTypes;

          if (
            this.workflow &&
            this.workflow.equipmentItem &&
            this.workflow.equipmentItem.title
          ) {
            this.filteredStatusList = this.getValidStatusForEquipment(
              this.workflow.equipmentItem,
            );
          }
        },
      );

    // Select and control subscriptions
    if (this.addInstalledEquipment || this.addRecommendedEquipment) {
      this.categoryControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((catId: number) => {
          this.categoryEquipmentList = [];
          this.equipmentLibrary.forEach(
            (category: EquipmentCategoryInterface) => {
              if (category.id === catId) {
                category.items.forEach((equipmentItem: EquipmentInterface) => {
                  this.categoryEquipmentList.push(
                    equipmentItem as EmployeeEquipmentInterface,
                  );
                });
              }
            },
          );
          this.itemControl.patchValue("");
        });

      this.itemControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((equipment: EquipmentInterface) => {
          // if we have a non-recommendable piece of equipment, do not allow recommended status
          // also reset the status on equipment change
          if (equipment && equipment.title) {
            this.workflow = {
              ...this.workflow,
              equipmentItem: {
                ...equipment,
                id: undefined,
                equipmentLibraryItemId: equipment.id,
                equipmentCategoryLabel: this.categoryList.find(
                  (item: { id: number; title: string }) => {
                    return item.id === equipment.categoryId;
                  },
                ).title,
                assetId: "",
                serialNumber: "",
              },
            };
            this.workAreaStatusFormGroup.enable();
            this.workAreaStatusFormGroup.patchValue({
              workAreaCtrl: "",
              statusCtrl: "",
            });
            this.currentStatus = {};
            this.filteredStatusList =
              this.getValidStatusForEquipment(equipment);
            this.descriptionControl.patchValue(
              this.getEquipmentDescription(equipment),
            );
          }
        });
    }

    this.workAreaControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((workAreaId: number) => {
        this.workAreas.forEach((workArea: AssessmentAnswerInterface) => {
          if (workArea.id === workAreaId) {
            this.workflow = {
              ...this.workflow,
              equipmentItem: {
                ...this.workflow.equipmentItem,
                workAreaId: workArea.id,
                workAreaLabel: workArea.label,
              },
            };
          }
        });
      });

    // Suscribe and respond to status changes
    this.statusControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((statusId: number) => {
        if (statusId > 0) {
          // if status doesn't change, do nothing
          if (
            this.currentStatus &&
            this.currentStatus.statusTypeId === statusId
          ) {
            return;
          }

          // if we are changing the status on an existing workflow
          // save the current status note and move to history
          if (this.currentStatus && this.currentStatus.id > 0) {
            this.currentStatus = {
              ...this.currentStatus,
              notes: stringEncodeToB64(this.statusNoteControl.value),
            };
            this._equipmentService
              .updateEmployeeEquipmentWorkflowStatus(
                this.workflow,
                this.currentStatus,
              )
              .pipe(take(1))
              .subscribe(
                (updatedWorkflow: EmployeeEquipmentWorkflowInterface) => {
                  if (!updatedWorkflow) {
                    return;
                  }
                },
              );
            // Insert into beginning of the history array
            this.statusHistory.unshift(this.currentStatus);
          }

          // get the template for the new selected status
          const statusTemplate: EmployeeEquipmentWorkflowStatusTypeInterface =
            this.statusList.find(
              (
                statusTypeItem: EmployeeEquipmentWorkflowStatusTypeInterface,
              ) => {
                return statusTypeItem.id === statusId;
              },
            );
          // Create new status from the template
          const newStatus: EmployeeEquipmentWorkflowStatusInterface = {
            statusTypeId: statusTemplate.id,
            statusTypeLabel: statusTemplate.label,
            realatedStateEnum: statusTemplate.relatedInstalledState.state,
            createdBy: {
              displayName: this.data.authUser.displayName,
            },
            createdDate: new Date(),
          };

          this.statusNoteControl.enable();

          if (
            this.workflow &&
            this.workflow.statusHistory &&
            this.workflow.statusHistory.length > 0
          ) {
            if (
              this.workflow.statusHistory[0].realatedStateEnum !==
              newStatus.realatedStateEnum
            ) {
              this.setDateFromStatus(newStatus);
            } else {
              this.setDateFromStatus(this.workflow.statusHistory[0]);
            }
          } else {
            this.setDateFromStatus(newStatus);
          }

          this.currentStatus = newStatus;
          this.statusNoteControl.setValue("");

          // adjust the form to show proper extended fields
          if (
            newStatus.realatedStateEnum === InstalledStateEnum.StateInstalled
          ) {
            this.showRecommendedFields = false;
            this.accessoryFieldsFormGroup.enable();
          } else {
            this.showRecommendedFields = true;
            this.accessoryFieldsFormGroup.disable();
          }
          this.fieldState = newStatus.realatedStateEnum;
        }
      });
  }

  private initializeFormsAndControls(): void {
    this.categoryControl = new FormControl({ value: "" }, Validators.required);
    this.itemControl = new FormControl({ value: "" }, Validators.required);
    this.workAreaControl = new FormControl({ value: "" }, Validators.required);
    this.statusControl = new FormControl({ value: "" }, Validators.required);

    this.categoryItemFormGroup = this._formBuilder.group({
      categoryCtrl: this.categoryControl,
      itemCtrl: this.itemControl,
    });

    this.workAreaStatusFormGroup = this._formBuilder.group({
      workAreaCtrl: this.workAreaControl,
      statusCtrl: this.statusControl,
    });

    this.workAreaStatusFormGroup.disable();

    this.accessoryFieldsFormGroup = this._formBuilder.group({
      assetIdControl: [""],
      serialNumberControl: [""],
      costControl: [""],
      colorControl: [""],
    });

    this.accessoryFieldsFormGroup.disable();

    this.statusNoteControl = new FormControl("");
    this.statusDateControl = new FormControl("");
    this.descriptionControl = new FormControl("");
  }

  private setFormControlValues(): void {
    this.categoryList = [];
    this.categoryEquipmentList = [];
    // If we are dealing with existing equipment (recommended or otherwise)
    if (this.editInstalledEquipment || this.editRecommendedEquipment) {
      this.categoryList = [
        {
          id: this.workflow.equipmentItem.categoryId,
          title: this.workflow.equipmentItem.equipmentCategoryLabel,
        },
      ];
      this.categoryEquipmentList = [this.workflow.equipmentItem];
      this.categoryItemFormGroup.patchValue({
        categoryCtrl: this.workflow.equipmentItem.categoryId,
        itemCtrl: this.workflow.equipmentItem,
      });
      this.categoryItemFormGroup.disable();

      // First item in status history is "current" each additional item is shown as a historic item
      this.currentStatus = this.workflow.statusHistory[0];
      this.statusHistory = [];
      for (let i: number = 1; i < this.workflow.statusHistory.length; i += 1) {
        this.statusHistory.push(this.workflow.statusHistory[i]);
      }

      this.filteredStatusList = this.getValidStatusForEquipment(
        this.workflow.equipmentItem,
      );
      this.workAreaStatusFormGroup.enable();
      this.workAreaStatusFormGroup.patchValue({
        workAreaCtrl: this.workflow.equipmentItem.workAreaId,
        statusCtrl: this.currentStatus.statusTypeId,
      });

      this.accessoryFieldsFormGroup.patchValue({
        assetIdControl: this.workflow.equipmentItem.assetId,
        serialNumberControl: this.workflow.equipmentItem.serialNumber,
        costControl: this.workflow.equipmentItem.cost,
        colorControl: this.workflow.equipmentItem.color,
      });

      this.statusNoteControl.setValue(
        stringDecodeToHtml(this.currentStatus.notes),
      );
      this.setDateFromStatus(this.currentStatus);

      if (
        this.currentStatus.realatedStateEnum ===
        InstalledStateEnum.StateInstalled
      ) {
        this.showRecommendedFields = false;
        this.accessoryFieldsFormGroup.enable();
      } else {
        this.showRecommendedFields = true;
        this.accessoryFieldsFormGroup.disable();
      }
      this.fieldState = this.currentStatus.realatedStateEnum;

      if (
        this.data.authUser &&
        this.currentStatus.createdBy.id === this.data.authUser.id
      ) {
        this.statusNoteControl.enable();
      } else {
        this.statusNoteControl.disable();
      }
      this.descriptionControl.patchValue(
        this.getEquipmentDescription(this.workflow.equipmentItem),
      );
    } else {
      // If we are adding new equipment we just need to setup the item selectors
      if (this.equipmentLibrary && this.equipmentLibrary.length > 0) {
        this.equipmentLibrary.forEach(
          (category: EquipmentCategoryInterface) => {
            if (category.items.length > 0) {
              this.categoryList.push({
                id: category.id,
                title: category.title,
              });
            }
          },
        );
      }
      this.categoryItemFormGroup.patchValue({
        categoryCtrl: "",
        itemCtrl: "",
      });
    }
  }

  private getValidStatusForEquipment(
    equipment: EmployeeEquipmentInterface,
  ): EmployeeEquipmentWorkflowStatusTypeInterface[] {
    if (!this.statusList) {
      return [];
    }
    return this.statusList.filter(
      (statusItem: EmployeeEquipmentWorkflowStatusTypeInterface) => {
        if (equipment.canRecommend || !this.addInstalledEquipment) {
          return statusItem;
        }
        if (
          statusItem.relatedInstalledState.state !==
            InstalledStateEnum.StateRecommended ||
          statusItem.id === this.currentStatus.statusTypeId
        ) {
          return statusItem;
        }
      },
    );
  }

  private setDateFromStatus(
    status: EmployeeEquipmentWorkflowStatusInterface,
  ): void {
    const statusEquipment: EmployeeEquipmentInterface =
      this.workflow.equipmentItem;
    if (statusEquipment) {
      switch (status.realatedStateEnum) {
        case InstalledStateEnum.StateInstalled:
          this.statusDateControl.setValue(
            statusEquipment.installedBy
              ? statusEquipment.installedDate
              : new Date(),
          );
          this.statusDateControl.enable();
          this.currentStatusUser = statusEquipment.installedBy
            ? statusEquipment.installedBy
            : this.data.authUser;
          break;
        case InstalledStateEnum.StateRecommended:
          this.statusDateControl.setValue(
            statusEquipment.recommendedBy
              ? statusEquipment.recommendedDate
              : new Date(),
          );
          this.statusDateControl.enable();
          this.currentStatusUser = statusEquipment.recommendedBy
            ? statusEquipment.recommendedBy
            : this.data.authUser;
          break;
        case InstalledStateEnum.StateRemoved:
          this.statusDateControl.setValue(
            statusEquipment.removedBy
              ? statusEquipment.removedDate
              : new Date(),
          );
          this.statusDateControl.enable();
          this.currentStatusUser = statusEquipment.removedBy
            ? statusEquipment.removedBy
            : this.data.authUser;
          break;
      }
    }
  }
}
